import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { productService } from "../services/service";

const EMPTY_PARAMS = {};
const TYPES_KEY = ["product-types"];
const CATEGORIES_KEY = ["product-categories"];
const PRODUCTS_KEY = ["products"];
const listData = (response) => response?.data?.data || [];
const listPage = (response) => response?.data || null;

export const useProducts = (initialParams = EMPTY_PARAMS) => {
  const client = useQueryClient();
  const [productTypesSearch, setProductTypesSearch] = useState("");
  const [productTypesStatusFilter, setProductTypesStatusFilter] = useState("all");
  const [productTypesPage, setProductTypesPage] = useState(1);
  const [productTypesPageSize, setProductTypesPageSize] = useState(5);

  const [productCategoriesSearch, setProductCategoriesSearch] = useState("");
  const [productCategoriesStatusFilter, setProductCategoriesStatusFilter] = useState("all");
  const [productCategoriesTypeFilter, setProductCategoriesTypeFilter] = useState(null);
  const [productCategoriesPage, setProductCategoriesPage] = useState(1);
  const [productCategoriesPageSize, setProductCategoriesPageSize] = useState(5);

  const [productsSearch, setProductsSearch] = useState("");
  const [productsStatusFilter, setProductsStatusFilter] = useState("all");
  const [productsPackageFilter, setProductsPackageFilter] = useState(null);
  const [productsCategoryFilter, setProductsCategoryFilter] = useState(null);
  const [productsPage, setProductsPage] = useState(1);
  const [productsPageSize, setProductsPageSize] = useState(5);

  const typeParams = useMemo(
    () => ({
      page: productTypesPage,
      per_page: productTypesPageSize,
      search: productTypesSearch || undefined,
      status: productTypesStatusFilter === "all" ? undefined : productTypesStatusFilter,
      ...initialParams,
    }),
    [productTypesPage, productTypesPageSize, productTypesSearch, productTypesStatusFilter, initialParams]
  );

  const categoryParams = useMemo(
    () => ({
      page: productCategoriesPage,
      per_page: productCategoriesPageSize,
      search: productCategoriesSearch || undefined,
      status: productCategoriesStatusFilter === "all" ? undefined : productCategoriesStatusFilter,
      product_type_id: productCategoriesTypeFilter || undefined,
      ...initialParams,
    }),
    [productCategoriesPage, productCategoriesPageSize, productCategoriesSearch, productCategoriesStatusFilter, productCategoriesTypeFilter, initialParams]
  );

  const productParams = useMemo(
    () => ({
      page: productsPage,
      per_page: productsPageSize,
      search: productsSearch || undefined,
      status: productsStatusFilter === "all" ? undefined : productsStatusFilter,
      package_type: productsPackageFilter || undefined,
      product_category_id: productsCategoryFilter || undefined,
      ...initialParams,
    }),
    [productsPage, productsPageSize, productsSearch, productsStatusFilter, productsPackageFilter, productsCategoryFilter, initialParams]
  );

  const typesQuery = useQuery({
    queryKey: [...TYPES_KEY, typeParams],
    queryFn: () => productService.getProductTypes(typeParams),
    staleTime: Infinity,
    keepPreviousData: true,
  });

  const categoriesQuery = useQuery({
    queryKey: [...CATEGORIES_KEY, categoryParams],
    queryFn: () => productService.getProductCategories(categoryParams),
    staleTime: Infinity,
    keepPreviousData: true,
  });

  const productsQuery = useQuery({
    queryKey: [...PRODUCTS_KEY, productParams],
    queryFn: () => productService.getProducts(productParams),
    staleTime: Infinity,
    keepPreviousData: true,
  });

  const invalidate = useCallback(
    (key) => client.invalidateQueries({ queryKey: key }),
    [client]
  );
  const mutate = useCallback(
    async (action, keys) => {
      const response = await action();
      await Promise.all(keys.map(invalidate));
      return response;
    },
    [invalidate]
  );

  return {
    // TYPES
    productTypes: listData(typesQuery.data),
    productTypesLoading: typesQuery.isLoading,
    productTypesFetching: typesQuery.isFetching,
    productTypesError: typesQuery.error,
    productTypesPagination: listPage(typesQuery.data),
    productTypesSearch, setProductTypesSearch,
    productTypesStatusFilter, setProductTypesStatusFilter,
    productTypesPage, setProductTypesPage,
    productTypesPageSize, setProductTypesPageSize,
    fetchProductTypes: typesQuery.refetch,
    refreshProductTypes: typesQuery.refetch,
    createProductType: (data) => mutate(() => productService.createProductType(data), [TYPES_KEY]),
    updateProductType: (id, data) => mutate(() => productService.updateProductType(id, data), [TYPES_KEY, CATEGORIES_KEY]),
    toggleProductTypeStatus: (id) => mutate(() => productService.toggleProductTypeStatus(id), [TYPES_KEY, CATEGORIES_KEY]),

    // CATEGORIES
    productCategories: listData(categoriesQuery.data),
    productCategoriesLoading: categoriesQuery.isLoading,
    productCategoriesFetching: categoriesQuery.isFetching,
    productCategoriesError: categoriesQuery.error,
    productCategoriesPagination: listPage(categoriesQuery.data),
    productCategoriesSearch, setProductCategoriesSearch,
    productCategoriesStatusFilter, setProductCategoriesStatusFilter,
    productCategoriesTypeFilter, setProductCategoriesTypeFilter,
    productCategoriesPage, setProductCategoriesPage,
    productCategoriesPageSize, setProductCategoriesPageSize,
    fetchProductCategories: categoriesQuery.refetch,
    refreshProductCategories: categoriesQuery.refetch,
    createProductCategory: (data) => mutate(() => productService.createProductCategory(data), [CATEGORIES_KEY, PRODUCTS_KEY]),
    updateProductCategory: (id, data) => mutate(() => productService.updateProductCategory(id, data), [CATEGORIES_KEY, PRODUCTS_KEY]),
    toggleProductCategoryStatus: (id) => mutate(() => productService.toggleProductCategoryStatus(id), [CATEGORIES_KEY, PRODUCTS_KEY]),
    getCategoriesByProductType: (id) => productService.getProductCategoriesByProductType(id),

    // PRODUCTS
    products: listData(productsQuery.data),
    productsLoading: productsQuery.isLoading,
    productsFetching: productsQuery.isFetching,
    productsError: productsQuery.error,
    productsPagination: listPage(productsQuery.data),
    productsSearch, setProductsSearch,
    productsStatusFilter, setProductsStatusFilter,
    productsPackageFilter, setProductsPackageFilter,
    productsCategoryFilter, setProductsCategoryFilter,
    productsPage, setProductsPage,
    productsPageSize, setProductsPageSize,
    fetchProducts: productsQuery.refetch,
    refreshProducts: productsQuery.refetch,
    createProduct: (data) => mutate(() => productService.createProduct(data), [PRODUCTS_KEY]),
    updateProduct: (id, data) => mutate(() => productService.updateProduct(id, data), [PRODUCTS_KEY]),
    toggleProductStatus: (id) => mutate(() => productService.toggleProductStatus(id), [PRODUCTS_KEY]),
    getPackageTypes: () => productService.getPackageTypes(),
    getProductsByCategory: (id) => productService.getProductByCategory(id),
    refreshAll: () =>
      Promise.all([
        typesQuery.refetch(),
        categoriesQuery.refetch(),
        productsQuery.refetch(),
      ]),
  };
};